import { Component, Input, OnInit, DoCheck, ViewChild, TemplateRef } from '@angular/core';
import { SurveyService } from '../../../services/surveys.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-survey-custom-charts',
  standalone: false,
  templateUrl: './survey-custom-charts.component.html',
  styleUrls: ['./survey-custom-charts.component.scss'],
})
export class SurveyCustomChartsComponent implements OnInit, DoCheck {
  @Input() survey: any;
  @ViewChild('helpDialog') helpDialog!: TemplateRef<any>;
  @ViewChild('editDialog') editDialog!: TemplateRef<any>;
  @ViewChild('infoDialog') infoDialog!: TemplateRef<any>;

  charts: any[] = [];
  loading = false;
  creating = false;

  questionDimensions: any[] = [];
  customFieldDimensions: any[] = [];

  newChartTitle = '';
  newChartType = 'bar';
  selectedXAxis: any = null;
  selectedGroupBy: any = null;

  selectedChartToEdit: any = null;
  editChartTitle = '';
  editChartType = 'bar';

  selectedChartForInfo: any = null;

  chartDataMap: { [id: string]: any } = {};

  private _lastSig = '';

  constructor(
    private surveyService: SurveyService,
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {}

  openHelpDialog() {
    this.dialog.open(this.helpDialog, {
      width: '550px',
      maxHeight: '90vh'
    });
  }

  ngOnInit() {
    this.extractDimensions();
    this.loadCharts();
  }

  ngDoCheck() {
    const qLen = this.survey?.questions?.length || 0;
    const cfLen = this.survey?.team_member_custom_fields?.length || 0;
    const sig = `${qLen}_${cfLen}`;
    if (sig !== this._lastSig && (qLen > 0 || cfLen > 0)) {
      this._lastSig = sig;
      this.extractDimensions();
    }
  }

  extractDimensions() {
    this.questionDimensions = [];
    this.customFieldDimensions = [];

    if (this.survey?.team_member_custom_fields) {
      this.survey.team_member_custom_fields.forEach((cf: any) => {
        this.customFieldDimensions.push({ label: cf.name, value: { type: 'custom_field', id: cf.id } });
      });
    }

    if (this.survey?.questions) {
      this.survey.questions.forEach((q: any) => {
        if (['CHECKBOX', 'MULTIPLE_CHOICE', 'QUANTITATIVE', 'CONSENSUS', 'GRID', 'CLASSIFICATION', 'RANKING', 'WEIGHTED'].includes(q.typology)) {
          this.questionDimensions.push({ label: q.question, value: { type: 'question', id: q.id } });
        }
      });
    }
  }

  loadCharts() {
    if (!this.survey?.id) return;
    this.loading = true;
    this.surveyService.getCustomCharts(this.survey.id, (res) => {
      this.charts = res;
      this.loading = false;
      this.charts.forEach(c => this.loadChartData(c));
    }, () => this.loading = false);
  }

  loadChartData(chart: any) {
    this.surveyService.getCustomChartData(this.survey.id, chart.id, (res: any) => {
      const xAxisSetting = chart.settings?.x_axis;
      let isConsensus = false;
      if (xAxisSetting && xAxisSetting.type === 'question' && this.survey?.questions) {
        const q = this.survey.questions.find((x: any) => x.id === xAxisSetting.id);
        if (q && q.typology === 'CONSENSUS') {
          isConsensus = true;
        }
      }

      const mappedSeries = res.series.map((s: any) => {
        if (chart.chart_type !== 'pie' && chart.chart_type !== 'heatmap') {
          s.type = chart.chart_type; // override backend bar type if line or scatter
        } else if (chart.chart_type === 'pie') {
          if (s.data) {
            s.data = s.data.map((item: any) => {
              let name = item.name;
              const matches = name.match(/^((?:components|generic)\.[a-zA-Z0-9.-]+)(.*)$/);
              if (matches) {
                const key = matches[1];
                const suffix = matches[2];
                name = this.translateService.instant(key) + suffix;
              }
              return { ...item, name };
            });
          }
        }
        return s;
      });

      const translatedCategories = res.categories ? res.categories.map((cat: any) => {
        if (typeof cat === 'string' && (cat.startsWith('components.') || cat.startsWith('generic.'))) {
          return this.translateService.instant(cat);
        }
        return cat;
      }) : [];

      if (chart.chart_type === 'heatmap') {
        const xData = translatedCategories;
        const yData = mappedSeries.map((s: any) => s.name);
        const heatmapData: any[] = [];
        let maxVal = 0;

        mappedSeries.forEach((s: any, yIdx: number) => {
          if (s.data) {
            s.data.forEach((val: any, xIdx: number) => {
              const numVal = Number(val) || 0;
              if (numVal > maxVal) {
                maxVal = numVal;
              }
              heatmapData.push([xIdx, yIdx, numVal]);
            });
          }
        });

        this.chartDataMap[chart.id] = {
          tooltip: {
            position: 'top',
            formatter: (params: any) => {
              const xLabel = xData[params.data[0]];
              const yLabel = yData[params.data[1]];
              const val = params.data[2];
              return `${yLabel}<br/>${xLabel}: <b>${val}</b>`;
            }
          },
          grid: {
            top: '10%',
            bottom: '15%',
            left: '15%',
            right: '5%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: xData,
            splitArea: { show: true },
            axisLabel: { interval: 0, rotate: xData.length > 3 ? 15 : 0 }
          },
          yAxis: {
            type: 'category',
            data: yData,
            splitArea: { show: true }
          },
          visualMap: {
            min: 0,
            max: maxVal || 10,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: '0%'
          },
          series: [{
            name: chart.title,
            type: 'heatmap',
            data: heatmapData,
            label: { show: true }
          }]
        };
      } else {
        this.chartDataMap[chart.id] = {
          tooltip: {
            trigger: chart.chart_type === 'pie' ? 'item' : 'axis',
            formatter: isConsensus
              ? (chart.chart_type === 'pie'
                  ? '{b}: {c}%'
                  : (params: any) => {
                      if (!params || params.length === 0) return '';
                      let html = `<div style="font-weight: bold; margin-bottom: 4px;">${params[0].axisValueLabel}</div>`;
                      params.forEach((p: any) => {
                        const val = p.value !== undefined ? p.value : 0;
                        const marker = p.marker || '';
                        html += `<div style="display: flex; justify-content: space-between; align-items: center; gap: 15px;">
                          <span>${marker}${p.seriesName}</span>
                          <span style="font-weight: bold;">${val}%</span>
                        </div>`;
                      });
                      return html;
                    })
              : undefined
          },
          legend: { data: mappedSeries.map((s: any) => s.name) },
          xAxis: chart.chart_type === 'pie' ? undefined : {
            type: 'category',
            data: translatedCategories,
            axisLabel: {
              interval: 0,
              rotate: (isConsensus || translatedCategories.length > 3) ? 15 : 0
            }
          },
          yAxis: chart.chart_type === 'pie' ? undefined : {
            type: 'value',
            axisLabel: isConsensus ? {
              formatter: '{value}%'
            } : undefined
          },
          series: mappedSeries
        };
      }
    });
  }

  createNewChart() {
    if (!this.newChartTitle || !this.selectedXAxis) return;
    this.creating = true;

    const payload = {
      title: this.newChartTitle,
      chart_type: this.newChartType,
      settings: {
        x_axis: this.selectedXAxis,
        group_by: this.selectedGroupBy || null
      }
    };

    this.surveyService.createCustomChart(this.survey.id, payload, () => {
      this.creating = false;
      this.newChartTitle = '';
      this.selectedXAxis = null;
      this.selectedGroupBy = null;
      this.loadCharts();
    }, () => this.creating = false);
  }

  deleteChart(id: any) {
    if (confirm(this.translateService.instant('components.custom-charts.delete-confirm'))) {
      this.surveyService.deleteCustomChart(this.survey.id, id, () => {
        this.loadCharts();
      });
    }
  }

  openEditDialog(chart: any) {
    this.selectedChartToEdit = chart;
    this.editChartTitle = chart.title;
    this.editChartType = chart.chart_type;
    this.dialog.open(this.editDialog, {
      width: '500px',
      maxHeight: '90vh'
    });
  }

  saveChart(chart: any) {
    if (!chart || !this.editChartTitle.trim()) return;
    this.loading = true;
    this.surveyService.updateCustomChart(
      this.survey.id,
      chart.id,
      { title: this.editChartTitle, chart_type: this.editChartType },
      (updated: any) => {
        chart.title = updated.title;
        chart.chart_type = updated.chart_type;
        this.loadChartData(chart);
        this.selectedChartToEdit = null;
        this.loading = false;
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  openInfoDialog(chart: any) {
    this.selectedChartForInfo = chart;
    this.dialog.open(this.infoDialog, {
      width: '450px',
      maxHeight: '90vh'
    });
  }

  getDimensionLabel(target: any): string {
    if (!target) return this.translateService.instant('components.custom-charts.no-grouping');
    
    // Check custom fields
    const cfMatch = this.customFieldDimensions.find(
      d => d.value.type === target.type && d.value.id === target.id
    );
    if (cfMatch) return cfMatch.label;

    // Check questions
    const qMatch = this.questionDimensions.find(
      d => d.value.type === target.type && 
           d.value.id === target.id && 
           d.value.sub_id === target.sub_id &&
           d.value.sub_id_2 === target.sub_id_2
    );
    if (qMatch) return qMatch.label;

    return 'N/A';
  }
}
